import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { LuUpload, LuImageOff } from "react-icons/lu";

const C = {
  bg: "#f8fafc",
  border: "#e2e8f0",
  navy: "#1e3a5f",
  teal: "#0891b2",
  slate: "#64748b",
  white: "#ffffff",
  red: "#e63946",
};

function SectionCard({ title, children }) {
  return (
    <div
      style={{
        backgroundColor: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: "10px",
        padding: "24px",
        marginBottom: "24px",
      }}
    >
      <h2
        style={{
          fontSize: "16px",
          fontWeight: "700",
          color: C.navy,
          margin: "0 0 20px 0",
          paddingBottom: "12px",
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

function UploadButton({ label, accept, onFile, loading }) {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept || "image/*"}
        style={{ display: "none" }}
        onChange={(e) => {
          if (e.target.files[0]) onFile(e.target.files[0]);
          e.target.value = "";
        }}
      />
      <button
        onClick={() => ref.current?.click()}
        disabled={loading}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          backgroundColor: loading ? C.slate : C.teal,
          color: C.white,
          border: "none",
          borderRadius: "6px",
          padding: "8px 14px",
          fontSize: "13px",
          fontWeight: "600",
          cursor: loading ? "not-allowed" : "pointer",
        }}
      >
        <LuUpload size={14} />
        {loading ? "Uploading…" : label}
      </button>
    </>
  );
}

function LogoSection() {
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/company-template/`)
      .then((r) => setLogoUrl(r.data.logo_url || null))
      .catch(() => {});
  }, []);

  const handleFile = async (file) => {
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const r = await axios.post(`${API}/company-template/`, fd);
      setLogoUrl(r.data.logo_url || null);
    } catch (e) {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <SectionCard title="Company Logo">
      <div style={{ display: "flex", alignItems: "flex-start", gap: "24px", flexWrap: "wrap" }}>
        <div
          style={{
            width: "200px",
            height: "100px",
            border: `1px dashed ${C.border}`,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: C.bg,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Company logo"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <LuImageOff size={28} color={C.slate} />
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <p style={{ margin: 0, fontSize: "13px", color: C.slate }}>
            Used on inspection PDF reports. Recommended size: 500×211 px PNG.
          </p>
          <UploadButton label={logoUrl ? "Replace Logo" : "Upload Logo"} onFile={handleFile} loading={uploading} />
          {error && <span style={{ fontSize: "12px", color: C.red }}>{error}</span>}
        </div>
      </div>
    </SectionCard>
  );
}

function TechSignaturesSection() {
  const [techs, setTechs] = useState([]);
  const [sigsMap, setSigsMap] = useState({});
  const [uploading, setUploading] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/technicians/`),
      axios.get(`${API}/tech-signatures/`),
    ]).then(([techRes, sigRes]) => {
      setTechs(techRes.data);
      const map = {};
      for (const s of sigRes.data) {
        map[s.tech] = { url: s.signature_url, name: s.tech_name };
      }
      setSigsMap(map);
    }).catch(() => {});
  }, []);

  const handleFile = async (techId, file) => {
    setUploading((p) => ({ ...p, [techId]: true }));
    setErrors((p) => ({ ...p, [techId]: "" }));
    try {
      const fd = new FormData();
      fd.append("tech", techId);
      fd.append("signature", file);
      await axios.post(`${API}/tech-signatures/`, fd);
      // Refresh signatures
      const sigRes = await axios.get(`${API}/tech-signatures/`);
      const map = {};
      for (const s of sigRes.data) {
        map[s.tech] = { url: s.signature_url, name: s.tech_name };
      }
      setSigsMap(map);
    } catch (e) {
      setErrors((p) => ({ ...p, [techId]: "Upload failed." }));
    } finally {
      setUploading((p) => ({ ...p, [techId]: false }));
    }
  };

  if (!techs.length) {
    return (
      <SectionCard title="Tech Signatures">
        <p style={{ color: C.slate, fontSize: "14px", margin: 0 }}>Loading technicians…</p>
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Tech Signatures">
      <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: C.slate }}>
        Signatures are embedded in the PDF inspection report under the technician's name.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {techs.map((tech) => {
          const fullName = [tech.first_name, tech.last_name].filter(Boolean).join(" ") || tech.username;
          const existing = sigsMap[tech.id];
          return (
            <div
              key={tech.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "14px 16px",
                borderRadius: "8px",
                border: `1px solid ${C.border}`,
                backgroundColor: C.bg,
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: "160px" }}>
                <div style={{ fontSize: "14px", fontWeight: "600", color: C.navy }}>{fullName}</div>
                <div style={{ fontSize: "12px", color: C.slate, marginTop: "2px" }}>@{tech.username}</div>
              </div>
              <div
                style={{
                  width: "140px",
                  height: "56px",
                  border: `1px dashed ${C.border}`,
                  borderRadius: "6px",
                  backgroundColor: C.white,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {existing?.url ? (
                  <img
                    src={existing.url}
                    alt={`${fullName} signature`}
                    style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <span style={{ fontSize: "11px", color: C.slate }}>No signature</span>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <UploadButton
                  label={existing ? "Replace" : "Upload"}
                  onFile={(f) => handleFile(tech.id, f)}
                  loading={!!uploading[tech.id]}
                />
                {errors[tech.id] && (
                  <span style={{ fontSize: "12px", color: C.red }}>{errors[tech.id]}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

export function TemplatesPage() {
  return (
    <div
      style={{
        padding: "24px",
        maxWidth: "860px",
        margin: "0 auto",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <h1
        style={{
          fontSize: "20px",
          fontWeight: "700",
          color: C.navy,
          margin: "0 0 24px 0",
        }}
      >
        Form Assets
      </h1>
      <LogoSection />
      <TechSignaturesSection />
    </div>
  );
}
